/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

// JS bridge to the native MotionActivityPlugin (iOS: ios/App/App/,
// Android: android/.../MotionActivityPlugin.kt). Both platforms expose the
// SAME method/event surface and emit the same normalized ActivityState
// strings, so this interface is platform-agnostic.
//
// Web no-op: registerPlugin returns a proxy that throws "not implemented" at
// call time; the useMotionActivity hook guards every call with a platform
// check so callers never see that error in production.

import {
  type PluginListenerHandle,
  registerPlugin,
} from "@capacitor/core";
import type {
  ActivityConfidence,
  ActivityState,
} from "@/utils/activity/activityState";

/** Permission state as reported by the native plugin. Mirrors the Capacitor
 *  PermissionState vocabulary plus `unavailable` for devices/sims without a
 *  motion coprocessor (CMMotionActivityManager.isActivityAvailable() == false
 *  / no ActivityRecognition hardware). */
export type ActivityPermissionStatus =
  | "granted"
  | "denied"
  | "prompt"
  | "restricted"
  | "unavailable"
  | "unknown";

export interface ActivityChangeEvent {
  state: ActivityState;
  confidence: ActivityConfidence;
}

export interface MotionActivityPlugin {
  checkPermissions(): Promise<{ status: ActivityPermissionStatus }>;
  requestPermissions(): Promise<{ status: ActivityPermissionStatus }>;
  start(): Promise<{ started: boolean }>;
  stop(): Promise<{ stopped: boolean }>;
  addListener(
    eventName: "activityChange",
    listenerFunc: (event: ActivityChangeEvent) => void,
  ): Promise<PluginListenerHandle>;
  removeAllListeners(): Promise<void>;
}

export const MotionActivity =
  registerPlugin<MotionActivityPlugin>("MotionActivity");
